import { Box, Text, Flex, Icon } from '@chakra-ui/react';
import { BsBell, BsClipboardCheck, BsChat } from 'react-icons/bs';
import { CiChat1, CiGlobe } from 'react-icons/ci';
import { FiPhone } from 'react-icons/fi';
import { MdOutlineEmail } from 'react-icons/md';
import { formatDuration } from '../../../utils/DefaultFunction';




const PerformanceBar = ({ phases, deliveryRate, sampleMessages }) => {
    const totalDuration = phases.reduce((sum, phase) => sum + (phase.duration || 0), 0);
    const phaseColors = { 1: '#2b7fff', 2: '#ff6900', 3: '#ad46ff' };

    const enrichedPhases = phases.map(p => ({
        ...p,
        titleColor:
            p.id === 1 ? "#193cb8" :
                p.id === 2 ? "#9f2d00" :
                    "#6b21a8",  // purple for phase 3
        valueColor:
            p.id === 1 ? "var(--brand-900)" :
                p.id === 2 ? "#7e2a0c" :
                    "#59168b",  // strong purple
        bgColor:
            p.id === 1 ? "var(--brand-tint)" :
                p.id === 2 ? "#fff7ed" :
                    "#faf5ff",  // soft purple background
    }));



    return (
        <Box mt={2}>
            {/* Phase Label indicators */}
            <Flex justify="space-between" mb={1} color="#64748b" fontSize="xs" fontWeight="semibold">
                {phases.map(phase => {
                    if (phase.duration > 0) {
                        return (
                            <Flex alignItems={"center"}>
                                <Box width={"8px"} height={"8px"} bg={phaseColors[phase.id]} borderRadius={"full"} me={2} />
                                <Text key={phase.id}>Phase {phase.id} {phase?.phaseLable ? `(${phase?.phaseLable})` : ""}</Text>
                            </Flex>
                        )
                    }
                    return null;
                })}
            </Flex>

            {/* The main stacked bar with time labels */}
            <Flex h="20px" borderRadius="full" overflow="hidden" w="100%" align="center" bg="gray.100">
                {phases.map(phase => {
                    if (phase.duration > 0) {
                        const widthPercent = (phase.duration / totalDuration) * 100;

                        return (
                            <Flex
                                key={phase.id}
                                w={`${widthPercent < 1 ? 1 : widthPercent}%`}
                                bg={phaseColors[phase.id]}
                                h="100%"
                                align="center"
                                justify="center"
                            >
                                <Text color="white" fontWeight="normal" fontSize="xs">
                                    {formatDuration(phase.duration.toFixed(2))}
                                </Text>
                            </Flex>
                        );
                    }
                    return null;
                })}
            </Flex>

            {/* Detailed Labels and Averages below the bar */}
            <Flex justify="space-between" mt={2} gap={3}>
                {enrichedPhases?.map(phase => (
                    phase?.tracking ? <Box
                        key={phase.id}
                        flex="1"
                        textAlign="center"
                        p={2}
                        bg={phase.bgColor || "transparent"}  // ✅ new background per card
                        borderRadius="md"
                    >

                        <Flex justifyContent={"space-between"}>
                            <Text
                                fontSize="xs"
                                fontWeight="semibold"
                                color={phase.titleColor || "gray.700"} // ✅ title color
                            >
                                {phase.label}
                            </Text>
                            <Text
                                fontSize="xs"
                                fontWeight="semibold"
                                color={phase.valueColor || (phase.duration > 0 ? phaseColors[phase.id] : "gray.500")} // ✅ value color
                            >
                                {phase.duration > 0 ? `${formatDuration(phase.duration.toFixed(2))} avg` : "-"}
                            </Text>
                        </Flex>
                    </Box> : <Box
                        key={phase.id}
                        flex="1"
                        textAlign="center"
                        p={2}
                        bg={"#f1f5f980"}
                        borderRadius="md"
                    >

                        <Flex justifyContent={"center"} alignItems={"center"}>
                            <Text
                                fontSize="xs"
                                fontWeight="semibold"
                                color={"#64748b"}
                                textAlign={"center"}
                            >
                                {phase.label}
                            </Text>

                        </Flex>
                    </Box>

                ))}
            </Flex>


            {/* Delivery/Sample Rate Footer */}
            <Flex justify="flex-start" mt={3} fontSize="sm" color="gray.600">
                <Flex align="center" mr={4}>
                    <Box width={"8px"} height={"8px"} bg={"#00c951"} borderRadius={"full"} me={2} />

                    <Text fontWeight="semibold" fontSize={"xs"}>Delivery: {deliveryRate}%</Text>
                </Flex>
                <Flex align="center">
                    <Box width={"8px"} height={"8px"} bg={"#2b7fff"} borderRadius={"full"} me={2} />

                    <Text fontWeight="semibold" fontSize={"xs"}>Sample: {sampleMessages} msgs</Text>
                </Flex>
            </Flex>
        </Box>
    );
};



const ChannelDetailCard = ({ channel, totalAvgTime, phases, sampleMessages, deliveryRate }) => {
    let ChannelIcon, iconColor;

    switch (channel) {
        case 'SMS':
            ChannelIcon = CiChat1;
            iconColor = '#3b82f6';
            break;
        case 'WhatsApp':
            ChannelIcon = BsChat;
            iconColor = '#10b981';
            break;
        case 'Push Notification':
            ChannelIcon = BsBell;
            iconColor = '#ef4444';
            break;
        case 'E-Mail':
            ChannelIcon = MdOutlineEmail;
            iconColor = '#f59e0b';
            break;
        case 'In-App Notification':
            ChannelIcon = CiGlobe;
            iconColor = '#8b5cf6';
            break;
        case 'Voice (IVR)':
            ChannelIcon = FiPhone;
            iconColor = 'var(--brand-primary)';
            break;
        default:
            ChannelIcon = BsClipboardCheck;
            iconColor = 'gray.500';
    }

    return (
        <Box mb={6}>

            {/* Header: Channel Name, Total Time, 24h Messages */}
            <Flex justify="space-between" align="center" mb={4}>
                <Flex align="center">
                    <Icon as={ChannelIcon} w={4} h={4} color={iconColor} mr={2} />
                    <Text fontSize="md" fontWeight="semibold">
                        {channel}
                    </Text>
                    {totalAvgTime !== 'N/A' && (
                        <Box px={2} py={1} bg="#f1f5f9" ml={4} borderRadius={"5px"}>
                            <Text fontSize="xs" color="64748b" >
                                Total: {formatDuration(totalAvgTime)}
                            </Text>
                        </Box>

                    )}
                </Flex>

                <Box textAlign="right">
                    <Text fontSize="sm" color="gray.500">
                        24h Messages
                    </Text>
                    <Text fontSize="xl" fontWeight="bold">
                        {sampleMessages}
                    </Text>
                </Box>
            </Flex>

            {/* Render the Stacked Performance Bar */}
            <PerformanceBar phases={phases} deliveryRate={deliveryRate} sampleMessages={sampleMessages} />
        </Box>
    );
};


const DetailedChannelPerformanceSummary = ({ data }) => {


    return (
        <Box p={2}>


            {data?.map((data) => (
                <ChannelDetailCard
                    key={data.channel}
                    channel={data.channel}
                    totalAvgTime={data.totalAvgTime}
                    sampleMessages={data.totalMessage}
                    deliveryRate={data.deliveryRate}
                    phases={data.phases}
                />
            ))}
        </Box>
    );
};

export default DetailedChannelPerformanceSummary;